import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, limit, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import { toast } from 'react-hot-toast';
import { useAdminAuditLog } from '../hooks/useAdminAuditLog';
import {
  Trash2,
  Archive,
  MoreVertical,
  Users,
  AlertTriangle,
  Download,
  Filter,
  X,
  Building2,
  TrendingUp,
  Activity,
  FileText,
  Settings,
  ShieldCheck,
} from 'lucide-react';

// Unified data model
interface Company {
  id: string;
  companyName?: string;
  name?: string;
  abn?: string;
  acl?: string;
  email?: string;
  phone?: string;
  address?: string;
  state?: string;
  status?: string;
  brokerCount?: number;
  activeUsers?: number;
  monthlyRevenue?: number;
  createdAt?: any;
  updatedAt?: any;
}

type StatusFilter = 'all' | 'active' | 'inactive' | 'archived' | 'suspended';
type DeleteMode = 'archive' | 'delete';
type ViewMode = 'table' | 'cards';

interface DeleteConfirmation {
  show: boolean;
  mode: DeleteMode;
  companyIds: string[];
  companyNames: string[];
  totalUsers: number;
  deleteUsers: boolean;
}

const Companies: React.FC = () => {
  const { logAction } = useAdminAuditLog();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmation>({
    show: false,
    mode: 'archive',
    companyIds: [],
    companyNames: [],
    totalUsers: 0,
    deleteUsers: true,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const companiesRef = collection(db, 'companies');

      let companiesData: Company[] = [];

      // Build query based on filter
      let baseQuery;
      if (statusFilter !== 'all') {
        baseQuery = query(
          companiesRef,
          where('status', '==', statusFilter),
          limit(100)
        );
      } else {
        baseQuery = query(companiesRef, limit(100));
      }

      try {
        const snap = await getDocs(baseQuery);
        companiesData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Company));
      } catch (e) {
        const snap = await getDocs(query(companiesRef, limit(100)));
        companiesData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Company));
      }

      setCompanies(companiesData);

      // Log company list view to admin audit log (Privacy Act APP 13 compliance)
      void logAction({
        action: 'ADMIN_COMPANY_LIST_VIEWED',
        resource: 'companies',
        resourceName: 'Company List',
        metadata: {
          statusFilter,
          companyCount: companiesData.length,
          view: 'companies_list',
        },
      });
    } catch (e) {
      setError('Failed to load companies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, logAction]);

  useEffect(() => {
    void fetchCompanies();
  }, [statusFilter, fetchCompanies]);

  const filteredCompanies = companies.filter((company) => {
    if (!company) return false;
    const display = (company.companyName ?? company.name ?? '').toLowerCase();
    const abn = (company.abn ?? '').toLowerCase();
    const email = (company.email ?? '').toLowerCase();
    const acl = (company.acl ?? '').toLowerCase();
    const term = (searchTerm ?? '').toLowerCase();
    return display.includes(term) || abn.includes(term) || email.includes(term) || acl.includes(term);
  });

  // Log search actions with debouncing (Privacy Act APP 13 compliance)
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 3) return;

    const timer = setTimeout(() => {
      void logAction({
        action: 'ADMIN_COMPANY_SEARCHED',
        resource: 'companies',
        resourceName: 'Company Search',
        metadata: {
          searchTerm,
          resultCount: filteredCompanies.length,
          statusFilter,
        },
      });
    }, 1500); // Wait 1.5 seconds after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm, logAction, filteredCompanies.length, statusFilter]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'applications');
    const prev = document.documentElement.style.getPropertyValue('--app-body-bg');
    document.documentElement.style.setProperty('--app-body-bg', '#0f172a');
    return () => {
      document.documentElement.removeAttribute('data-theme');
      if (prev) document.documentElement.style.setProperty('--app-body-bg', prev);
      else document.documentElement.style.removeProperty('--app-body-bg');
    };
  }, []);

  const getStatusBadgeClass = (status?: string) => {
    switch ((status ?? '').toLowerCase()) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'inactive':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'suspended':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'archived':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCompanies.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCompanies.map(c => c.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Action handlers
  const handleArchiveSingle = (company: Company) => {
    setDeleteConfirm({
      show: true,
      mode: 'archive',
      companyIds: [company.id],
      companyNames: [company.companyName || company.name || company.id],
      totalUsers: company.activeUsers || 0,
      deleteUsers: true,
    });
    setShowActionMenu(null);
  };

  const handleDeleteSingle = (company: Company) => {
    setDeleteConfirm({
      show: true,
      mode: 'delete',
      companyIds: [company.id],
      companyNames: [company.companyName || company.name || company.id],
      totalUsers: company.activeUsers || 0,
      deleteUsers: true,
    });
    setShowActionMenu(null);
  };

  const handleBulkArchive = () => {
    const selected = companies.filter(c => selectedIds.has(c.id));
    setDeleteConfirm({
      show: true,
      mode: 'archive',
      companyIds: Array.from(selectedIds),
      companyNames: selected.map(c => c.companyName || c.name || c.id),
      totalUsers: selected.reduce((sum, c) => sum + (c.activeUsers || 0), 0),
      deleteUsers: true,
    });
  };

  const handleBulkDelete = () => {
    const selected = companies.filter(c => selectedIds.has(c.id));
    setDeleteConfirm({
      show: true,
      mode: 'delete',
      companyIds: Array.from(selectedIds),
      companyNames: selected.map(c => c.companyName || c.name || c.id),
      totalUsers: selected.reduce((sum, c) => sum + (c.activeUsers || 0), 0),
      deleteUsers: true,
    });
  };

  const handleExportSelected = () => {
    const selected = companies.filter(c => selectedIds.has(c.id));
    const csv = generateCSV(selected);
    downloadCSV(csv, 'companies-export.csv');
    toast.success(`Exported ${selected.length} companies`);
  };

  const confirmDeletion = async () => {
    if (deleteConfirm.mode === 'delete' && confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleteLoading(true);

    try {
      if (deleteConfirm.mode === 'archive') {
        if (deleteConfirm.companyIds.length === 1) {
          const archiveFn = httpsCallable(functions, 'archiveCompany');
          await archiveFn({
            companyId: deleteConfirm.companyIds[0],
            archiveUsers: deleteConfirm.deleteUsers,
          });
          toast.success('Company archived successfully');
        } else {
          const batchArchiveFn = httpsCallable(functions, 'batchArchiveCompanies');
          const result = await batchArchiveFn({
            companyIds: deleteConfirm.companyIds,
            archiveUsers: deleteConfirm.deleteUsers,
          }) as any;
          toast.success(`Archived ${result.data.succeeded} companies`);
        }
      } else {
        // Hard delete
        for (const companyId of deleteConfirm.companyIds) {
          const deleteFn = httpsCallable(functions, 'deleteCompany');
          await deleteFn({
            companyId,
            confirmationText: 'DELETE',
            deleteUsers: deleteConfirm.deleteUsers,
          });
        }
        toast.success('Companies deleted permanently');
      }

      // Refresh and reset
      await fetchCompanies();
      setSelectedIds(new Set());
      setDeleteConfirm({ show: false, mode: 'archive', companyIds: [], companyNames: [], totalUsers: 0, deleteUsers: true });
      setConfirmText('');
    } catch (err: any) {
      toast.error(err.message || 'Operation failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const generateCSV = (data: Company[]): string => {
    const headers = ['Company Name', 'ABN', 'ACL', 'Email', 'Phone', 'Status', 'Brokers'];
    const rows = data.map(c => [
      c.companyName || c.name || '',
      c.abn || '',
      c.acl || '',
      c.email || '',
      c.phone || '',
      c.status || '',
      c.brokerCount?.toString() || '0',
    ]);
    return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  };

  const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate stats
  const stats = {
    total: companies.length,
    active: companies.filter(c => c.status === 'active').length,
    archived: companies.filter(c => c.status === 'archived').length,
    totalBrokers: companies.reduce((sum, c) => sum + (c.brokerCount || 0), 0),
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="p-8">
        {/* Modern Gradient Header */}
        <div className="rounded-2xl p-8 mb-8 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-4 -right-4 w-64 h-64 bg-white/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-white/20 rounded-full blur-2xl animate-pulse delay-700" />
          </div>
          <div className="relative">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="w-10 h-10 text-white" />
                  <h1 className="text-4xl font-bold text-white">Company Management</h1>
                </div>
                <p className="text-white/90 text-lg mb-4">Manage and monitor all registered mortgage brokerage companies</p>
                <div className="flex gap-4 text-white/80 text-sm">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Full audit trail
                  </span>
                  <span className="flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Company-specific overrides
                  </span>
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Instant rollback
                  </span>
                </div>
              </div>
              <Link
                to="/companies/onboard"
                className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl shadow-lg hover:bg-white/30 transition-all duration-200 flex items-center gap-2 border border-white/30"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12M6 12h12" />
                </svg>
                Onboard New Company
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-500/30">
            <div className="flex items-center justify-between mb-2">
              <Building2 className="w-8 h-8 text-blue-400" />
              <span className="text-xs text-blue-300 uppercase tracking-wider">Total</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
            <p className="text-sm text-blue-200 mt-1">Companies</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-500/30">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-8 h-8 text-green-400" />
              <span className="text-xs text-green-300 uppercase tracking-wider">Active</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.active}</p>
            <p className="text-sm text-green-200 mt-1">Companies</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 backdrop-blur-sm rounded-xl p-6 border border-orange-500/30">
            <div className="flex items-center justify-between mb-2">
              <Archive className="w-8 h-8 text-orange-400" />
              <span className="text-xs text-orange-300 uppercase tracking-wider">Archived</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.archived}</p>
            <p className="text-sm text-orange-200 mt-1">Companies</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-500/30">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-purple-400" />
              <span className="text-xs text-purple-300 uppercase tracking-wider">Total</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.totalBrokers}</p>
            <p className="text-sm text-purple-200 mt-1">Brokers</p>
          </div>
        </div>

        {/* Filters and Search Section */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 mb-6 border border-slate-700/50">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by company name, ABN, ACL, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-11 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
              <svg className="absolute left-3.5 top-3.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="px-4 py-3 pl-11 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all appearance-none pr-10 min-w-[180px]"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
                <option value="suspended">Suspended</option>
              </select>
              <Filter className="absolute left-3.5 top-3.5 w-5 h-5 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl backdrop-blur-sm flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-white font-semibold text-lg">{selectedIds.size} selected</span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-slate-300 hover:text-white transition-colors text-sm"
              >
                Clear selection
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleExportSelected}
                className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-all flex items-center gap-2 backdrop-blur-sm border border-slate-600"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={handleBulkArchive}
                className="px-4 py-2 bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded-lg hover:bg-orange-500/30 transition-all flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                Archive ({selectedIds.size})
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-500/20 text-red-300 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-all flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete ({selectedIds.size})
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 backdrop-blur-sm">
            {error}
          </div>
        )}

        {/* Companies Table Card */}
        <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center gap-3 text-slate-400">
                <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-lg">Loading companies...</span>
              </div>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">
                {searchTerm ? 'No companies found matching your search.' : 'No companies registered yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-700/50">
                <thead className="bg-slate-800/80">
                  <tr>
                    <th className="px-6 py-4 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredCompanies.length && filteredCompanies.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-purple-500 focus:ring-purple-500"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Company Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">ABN</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">ACL</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Brokers</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                  {filteredCompanies.map((company) => {
                    const display = company.companyName || company.name || 'Unnamed Company';
                    const isSelected = selectedIds.has(company.id);

                    return (
                      <tr key={company.id} className={`hover:bg-slate-800/40 transition-all ${isSelected ? 'bg-purple-500/10 border-l-2 border-purple-500' : ''}`}>
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(company.id)}
                            className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-purple-500 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-white">{display}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300 font-mono">{company.abn || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300 font-mono">{company.acl || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300">{company.email || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full border uppercase tracking-wide ${getStatusBadgeClass(company.status)}`}>
                            {company.status || 'inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-300 flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span className="font-semibold">{company.brokerCount ?? 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to={`/subscribers/${company.id}/verify`}
                              title="Verify subscriber dashboards (read-only)"
                              className="p-1.5 text-blue-400 hover:text-blue-300 transition-colors hover:bg-blue-500/10 rounded-lg"
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </Link>
                            <Link
                              to={`/companies/${company.id}`}
                              className="px-3 py-1.5 text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors hover:bg-purple-500/10 rounded-lg"
                            >
                              View Details
                            </Link>
                            <div className="relative">
                              <button
                                onClick={() => setShowActionMenu(showActionMenu === company.id ? null : company.id)}
                                className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
                              >
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                              </button>

                              {showActionMenu === company.id && (
                                <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-10 overflow-hidden">
                                  <button
                                    onClick={() => handleArchiveSingle(company)}
                                    className="w-full px-4 py-3 text-left text-sm text-orange-400 hover:bg-slate-700/50 transition-colors flex items-center gap-2"
                                  >
                                    <Archive className="w-4 h-4" />
                                    Archive Company
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSingle(company)}
                                    className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-slate-700/50 transition-colors flex items-center gap-2 border-t border-slate-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete Permanently
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && filteredCompanies.length > 0 && (
          <div className="mt-6 text-center">
            <p className="text-slate-400">
              Showing <span className="font-semibold text-white">{filteredCompanies.length}</span> of <span className="font-semibold text-white">{companies.length}</span> companies
              {selectedIds.size > 0 && (
                <span className="ml-2">
                  • <span className="font-semibold text-purple-400">{selectedIds.size}</span> selected
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !deleteLoading && setDeleteConfirm({ ...deleteConfirm, show: false })} />
          <div className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl w-full max-w-lg border border-slate-700/50 shadow-2xl">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className={`p-3 rounded-xl ${deleteConfirm.mode === 'delete' ? 'bg-red-500/20' : 'bg-orange-500/20'}`}>
                  <AlertTriangle className={`w-6 h-6 ${deleteConfirm.mode === 'delete' ? 'text-red-400' : 'text-orange-400'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {deleteConfirm.mode === 'delete' ? 'Permanently Delete Companies?' : 'Archive Companies?'}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {deleteConfirm.mode === 'delete'
                      ? 'This action cannot be undone. All data will be permanently deleted.'
                      : 'Companies will be archived and can be restored later.'}
                  </p>
                </div>
                <button
                  onClick={() => setDeleteConfirm({ ...deleteConfirm, show: false })}
                  disabled={deleteLoading}
                  className="p-1 rounded hover:bg-slate-700 transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
                <div className="text-sm text-slate-300 mb-2 font-semibold">
                  Affected Companies ({deleteConfirm.companyIds.length}):
                </div>
                <ul className="text-sm text-slate-400 space-y-1 max-h-32 overflow-y-auto">
                  {deleteConfirm.companyNames.slice(0, 5).map((name, i) => (
                    <li key={i}>• {name}</li>
                  ))}
                  {deleteConfirm.companyNames.length > 5 && (
                    <li className="text-slate-500">... and {deleteConfirm.companyNames.length - 5} more</li>
                  )}
                </ul>
                <div className="mt-3 text-sm text-yellow-400 flex items-center gap-2 font-semibold">
                  <Users className="w-4 h-4" />
                  {deleteConfirm.totalUsers} users will be affected
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer p-3 hover:bg-slate-700/30 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={deleteConfirm.deleteUsers}
                    onChange={(e) => setDeleteConfirm({ ...deleteConfirm, deleteUsers: e.target.checked })}
                    disabled={deleteLoading}
                    className="w-4 h-4 rounded bg-slate-700 border-slate-600"
                  />
                  <span>{deleteConfirm.mode === 'delete' ? 'Also delete all user accounts' : 'Also deactivate all user accounts'}</span>
                </label>
              </div>

              {deleteConfirm.mode === 'delete' && (
                <div className="mb-6">
                  <label className="block text-sm text-slate-400 mb-2 font-semibold">
                    Type <span className="font-mono font-bold text-white bg-slate-700/50 px-2 py-1 rounded">DELETE</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    disabled={deleteLoading}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-red-500 focus:outline-none transition-colors disabled:opacity-50"
                    placeholder="DELETE"
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm({ ...deleteConfirm, show: false })}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeletion}
                  disabled={deleteLoading || (deleteConfirm.mode === 'delete' && confirmText !== 'DELETE')}
                  className={`flex-1 px-4 py-3 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold ${
                    deleteConfirm.mode === 'delete' ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                >
                  {deleteLoading ? 'Processing...' : deleteConfirm.mode === 'delete' ? 'Delete Permanently' : 'Archive'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close action menu */}
      {showActionMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActionMenu(null)}
        />
      )}
    </div>
  );
};

export default Companies;
